import React, { useState, useCallback, useEffect, useRef } from "react";
import { Grid, Skeleton, Tooltip } from "@mui/material";
import DataTable from "../../../Components/Common/datatable/DataTable";
import { MessagePopup } from "../../../Components/Common/messagePopup/MessagePopup";
import ApiService from "../../../utils/fetch";
import EditIcon from "@mui/icons-material/Edit";
import ButtonComponent from "../../../Components/Common/button/Button";
import { safeTimestamp } from "../../../Components/Common/DateFormatter";
import BlackWhiteSwitch from "../../BottlerForecast/MaterialPlaning/BlackWhiteSwitch";

const MFManualUpdate = ({ activeTab }) => {
  const [tableData, setTableData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(true);

  const gridRef = useRef(null);

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  /* ----------------------------------
   * DATA FETCH
   * ---------------------------------- */
  useEffect(() => {
    if (activeTab === 2) {
      fetchMFManualData();
    }
  }, [activeTab]);

  const fetchMFManualData = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get(
        "/v1/custom-market-forecast/getActiveBPPndSKU",
      );
      setTableData(response.data || []);
    } catch (error) {
      console.error("MF Manual Update fetch failed", error);
    } finally {
      setLoading(false);
    }
  };

  /* ----------------------------------
   * GRID EVENTS
   * ---------------------------------- */
  const onGridReady = useCallback((params) => {
    gridRef.current = params;
    params.api.sizeColumnsToFit();
  }, []);

  useEffect(() => {
    const resizeGrid = () => {
      if (gridRef.current?.api && tableData.length > 0) {
        gridRef.current.api.sizeColumnsToFit();
      }
    };
    resizeGrid();
    window.addEventListener("resize", resizeGrid);
    return () => window.removeEventListener("resize", resizeGrid);
  }, [tableData]);

  /* ----------------------------------
   * DATE COMPARATOR
   * ---------------------------------- */
  const dateComparator = (date1, date2) => {
    const t1 = date1 ? new Date(date1).getTime() : null;
    const t2 = date2 ? new Date(date2).getTime() : null;

    if (t1 === null && t2 === null) return 0;
    if (t1 === null) return -1;
    if (t2 === null) return 1;
    return t1 - t2;
  };

  /* ----------------------------------
   * COLUMN DEFINITIONS
   * ---------------------------------- */
  const columns = [
    {
      headerName: "",
      checkboxSelection: true,
      pinned: "left",
      width: 40,
      minWidth: 40,
      suppressMenu: true,
      hide: true,
    },
    {
      field: "bottler",
      headerName: "Bottler",
      width: 160,
      minWidth: 160,
      sortable: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <div>{p.value}</div>
        </Tooltip>
      ),
    },
    {
      field: "bottlerOwnerCode",
      headerName: "Bottler Owner Code",
      sortable: true,
      unSortIcon: true,
      width: 230,
      minWidth: 230,
      valueGetter: (params) => {
        const { bottlerOwnerCode, bottlerOwnerDescription } = params.data;
        return `${bottlerOwnerCode || ""}-${bottlerOwnerDescription || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "profitCenterDescription",
      headerName: "Customer Country",
      width: 200,
      minWidth: 200,
      sortable: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <div>{p.value}</div>
        </Tooltip>
      ),
    },
    {
      field: "sku",
      headerName: "Customer SKU",
      editable: true,
      width: 200,
      minWidth: 200,
      sortable: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <div>{p.value}</div>
        </Tooltip>
      ),
    },
    {
      field: "skuDescription",
      headerName: "SKU Description",
      editable: true,
      width: 200,
      minWidth: 200,
      sortable: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <div>{p.value}</div>
        </Tooltip>
      ),
    },
    {
      field: "bpp",
      headerName: "BPP Code",
      width: 180,
      minWidth: 180,
      sortable: true,
      valueGetter: (params) => {
        const { bpp } = params.data;
        return `${bpp || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "bpp",
      headerName: "BPP Description",
      width: 200,
      minWidth: 200,
      sortable: true,
      valueGetter: (params) => {
        const { bpp, bppDescription } = params.data;
        return `${bppDescription || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      headerName: "BPC Code",
      sortable: true,
      unSortIcon: true,
      width: 170,
      minWidth: 170,

      valueGetter: (params) => {
        const bppId = params.data?.bpc ?? "";

        if (!bppId) return "";

        return `${bppId}`;
      },

      cellRenderer: (params) => {
        if (!params.value) return null;

        return (
          <Tooltip title={params.value} arrow>
            <div>{params.value}</div>
          </Tooltip>
        );
      },

      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          { filter: "agTextColumnFilter" },
          { filter: "agSetColumnFilter" },
        ],
      },
    },
    {
      headerName: "BPC Description",
      sortable: true,
      unSortIcon: true,
      width: 250,
      minWidth: 250,

      valueGetter: (params) => {
        // const bppId = params.data?.bppCode ?? "";
        const bppDesc = params.data?.bpcDescription ?? "";

        if (!bppDesc) return "";

        return `${bppDesc}`;
      },

      cellRenderer: (params) => {
        if (!params.value) return null;

        return (
          <Tooltip title={params.value} arrow>
            <div>{params.value}</div>
          </Tooltip>
        );
      },

      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          { filter: "agTextColumnFilter" },
          { filter: "agSetColumnFilter" },
        ],
      },
    },
  ];

  const tableDownload = () => {
    gridRef.current.api.exportDataAsExcel({
      fileName: "MarketForecast_Active_BPP.xlsx",
      processCellCallback: (params) => {
        // Convert every value to plain text
        return params.value != null ? "'" + String(params.value) : "";
      },
    });
  };

  /* ----------------------------------
   * RENDER
   * ---------------------------------- */
  return (
    <div id="mfManualUpdatePage">
      <Grid container spacing={1} justifyContent="flex-end">
        <Grid item>
          <ButtonComponent
            label="Download"
            bgColor="black"
            color="white"
            onClick={tableDownload}
            fullWidth={false}
          />
        </Grid>
        <Grid item xs={12}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              height="75vh"
              animation="pulse"
              style={{ borderRadius: 8 }}
            />
          ) : (
            <DataTable
              ref={gridRef}
              columns={columns}
              rows={tableData}
              tableHeight="75vh"
              pagination={pagination}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              rowSelection="multiple"
              onGridReady={onGridReady}
              singleClickEdit
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default MFManualUpdate;
